import { useTranslator } from '@bluebeam/react-i18n';
import FileBrowserRow, {
  FileBrowserRowItem,
} from 'components/FileBrowser/FileBrowserRow';
import LoadingAnimation from 'components/LoadingAnimation';
import {
  StdList,
  StdListBody,
  StdListHeader,
  StdListHeaderCell,
  StdListHeaderCheckbox,
} from 'components/StdList';
import { fileBrowser } from 'copy/copy';
import { List } from 'immutable';
import React from 'react';

export type FileBrowserFile = {
  id: string;
  name: string;
  updatedAt: string;
};

export type FileBrowserFolder = {
  id: string;
  name: string;
  updatedAt: string;
};

export type FileBrowserDrive = {
  id: string;
  name: string;
};

export type FileBrowserSite = {
  id: string;
  name: string;
};

export type FileBrowserItem = {
  onSelect?: () => void;
  onClick?: () => void;
  failed?: boolean;
  isSelected: boolean;
  hideCheckbox?: boolean;
  fadedText?: boolean;
} & (
  | {
      type: 'file';
      value: FileBrowserFile;
    }
  | {
      type: 'folder';
      value: FileBrowserFolder;
      indeterminate: boolean;
    }
  | {
      type: 'drive';
      value: FileBrowserDrive;
      indeterminate: boolean;
    }
  | {
      type: 'site';
      value: FileBrowserSite;
      indeterminate: boolean;
    }
);

interface MessageRowProps {
  title: string;
  body: string;
}

const MessageRow: React.FC<MessageRowProps> = (props) => (
  <div className="flex row justify-start items-center bgd-white bb-3 black-2">
    <h3 className="py4 pl4 pr3 font-size-4 bold">{props.title}</h3>
    <p>{props.body}</p>
  </div>
);

interface MessageCenteredProps {
  title: string;
  body: string;
}

const MessageCentered: React.FC<MessageCenteredProps> = (props) => (
  <div className="h100 column justify-center items-center">
    <div className="bold font-size-5 pb1 center">{props.title}</div>
    <div
      className="center pb9"
      style={{ width: '13.875rem', margin: '0 auto' }}
    >
      {props.body}
    </div>
  </div>
);

export type FileBrowserMessage =
  | { type: 'loading'; text: string }
  | { type: 'row'; value: { title: string; body: string } }
  | { type: 'centered'; value: { title: string; body: string } };

interface FileBrowserProps {
  items: List<FileBrowserItem>;
  areAllSelected: boolean;
  message?: FileBrowserMessage;
  filter?: string;
  onSelectAllClick?: () => void;
}

const renderMessage = (message: FileBrowserMessage) => {
  if (message.type === 'centered') {
    return (
      <MessageCentered title={message.value.title} body={message.value.body} />
    );
  }

  if (message.type === 'loading') {
    return <LoadingAnimation showAnimation={false} pageName={message.text} />;
  }

  if (message.type === 'row') {
    return <MessageRow title={message.value.title} body={message.value.body} />;
  }

  return null;
};

const NoResultsMessage = () => {
  const { title, body } = fileBrowser.filter.noResults;

  return renderMessage({
    type: 'row',
    value: {
      title,
      body,
    },
  });
};

const FileBrowser: React.FC<FileBrowserProps> = (props: FileBrowserProps) => {
  const { filter, items, message, onSelectAllClick, areAllSelected } = props;
  const { translate } = useTranslator(['fileBrowser']);

  const gridTemplateColumns = '50px auto 150px';

  const createListRow = (item: FileBrowserItem) => {
    const {
      onSelect,
      onClick,
      value,
      type,
      isSelected,
      hideCheckbox,
      fadedText,
    } = item;

    const checkbox = (() => {
      if (hideCheckbox) return 'hidden';
      if (isSelected) return 'checked';
      if (
        (item.type === 'folder' ||
          item.type === 'drive' ||
          item.type === 'site') &&
        item.indeterminate
      )
        return 'indeterminate';
      return 'unchecked';
    })();

    return (
      <FileBrowserRow
        onClick={onClick}
        onSelect={onSelect}
        checkbox={checkbox}
        key={value.id}
        item={
          {
            value,
            type,
          } as FileBrowserRowItem
        }
        fadedText={fadedText}
      />
    );
  };

  const filteredItems = items.filter(
    (item) => !filter || item.value.name.includes(filter)
  );

  return (
    <StdList id="file-browser-list" gridTemplateColumns={gridTemplateColumns}>
      <StdListHeader>
        <StdListHeaderCheckbox
          id="file-browser-header-select-all-checkbox"
          indeterminate={false}
          checked={areAllSelected}
          onChange={onSelectAllClick}
          disabled={!onSelectAllClick}
        />
        <StdListHeaderCell text={translate('fileBrowser:name')} />
        <StdListHeaderCell text={translate('fileBrowser:lastModifiedDate')} />
      </StdListHeader>

      {(() => {
        if (message) return renderMessage(message);
        if (filter && filteredItems.count() === 0 && filter.trim() !== '')
          return <NoResultsMessage />;
        return (
          <StdListBody>
            {filteredItems.map((item) => createListRow(item))}
          </StdListBody>
        );
      })()}
    </StdList>
  );
};

export { FileBrowser };
